import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, LabelList, CartesianGrid, Tooltip,
} from "recharts";
import {
  Loader2, Receipt, Banknote, FileSpreadsheet, Search, Upload, ChevronLeft, ChevronRight, Wallet, CalendarDays, Zap,
  Wallet2, AlertTriangle, CircleCheck,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CustomerDrillDown } from "@/components/CustomerDrillDown";

const cr = (v) => (Number(v || 0) / 1e7).toFixed(2);
const lac = (v) => (Number(v || 0) / 1e7).toFixed(2);
const inr = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const smart = (v) => cr(v);

const SRC_BADGE = {
  milestone_billing_api: ["Milestone API", "bg-emerald-100 text-emerald-700"],
  milestone_billing_csv: ["Milestone CSV", "bg-teal-100 text-teal-700"],
  billing_excel_upload: ["Excel", "bg-slate-100 text-slate-600"],
};

const CLOSED_STATUSES = ["close", "closed", "paid"];
const todayStr = new Date().toISOString().slice(0, 10);

const payBadge = (r) => {
  const st = (r.payment_status || "").toLowerCase();
  if (CLOSED_STATUSES.includes(st)) return ["Closed", "bg-emerald-50 text-emerald-600"];
  if (r.due_date && r.due_date < todayStr && Number(r.outstanding || 0) > 0) {
    const days = Math.floor((Date.now() - new Date(r.due_date).getTime()) / 86400000);
    return [`Overdue ${days}d`, "bg-red-100 text-red-700"];
  }
  if (st) return [r.payment_status, "bg-amber-50 text-amber-700"];
  return ["—", "bg-slate-50 text-slate-400"];
};

function PaymentTracking({ pay, onCustomer }) {
  if (!pay) return null;
  const overdue = pay.top_overdue || [];
  return (
    <div className="grid lg:grid-cols-3 gap-5 mb-5" data-testid="billing-payment-tracking">
      <Card className="p-5" data-testid="billing-payment-summary">
        <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3 flex items-center gap-1.5">
          <Wallet2 className="h-4 w-4 text-brand" /> Payment tracking
        </h3>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
            <span className="text-xs font-medium text-emerald-800">Received</span>
            <span className="font-mono font-bold text-emerald-700" data-testid="billing-pay-received">{smart(pay.received)}</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2">
            <span className="text-xs font-medium text-amber-800">Open outstanding</span>
            <span className="font-mono font-bold text-amber-700" data-testid="billing-pay-outstanding">{smart(pay.open_outstanding)}</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2">
            <span className="text-xs font-medium text-red-800">Overdue ({pay.overdue_lines} invoices)</span>
            <span className="font-mono font-bold text-red-700" data-testid="billing-pay-overdue">{smart(pay.overdue)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(pay.by_status || []).map((s) => (
            <span key={s.status} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
              {s.status}: {smart(s.value)} · {s.lines} invoices
            </span>
          ))}
        </div>
        {!pay.has_due_dates && (
          <p className="text-[11px] text-slate-400 mt-3">
            Milestone hasn't sent due dates / receipt amounts yet — overdue tracking activates automatically once those fields arrive in the feed.
          </p>
        )}
      </Card>
      <Card className="p-5 lg:col-span-2" data-testid="billing-top-overdue">
        <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3 flex items-center gap-1.5">
          <AlertTriangle className="h-4 w-4 text-red-500" /> Top overdue invoices
        </h3>
        {overdue.length === 0 ? (
          <p className="text-sm text-slate-400 flex items-center gap-1.5 py-6 justify-center">
            <CircleCheck className="h-4 w-4 text-emerald-500" /> No overdue invoices in scope.
          </p>
        ) : (
          <div className="space-y-1.5">
            {overdue.map((o) => {
              const days = Math.floor((Date.now() - new Date(o.due_date).getTime()) / 86400000);
              return (
                <div key={o.invoice_no} className="flex items-center gap-3 text-sm rounded-lg border border-red-100 bg-red-50/40 px-3 py-1.5" data-testid={`billing-overdue-${o.invoice_no}`}>
                  <span className="font-mono text-xs text-slate-600">{o.invoice_no}</span>
                  <button type="button" onClick={() => onCustomer(o.customer)} className="text-slate-800 truncate flex-1 text-left hover:text-brand hover:underline" data-testid={`billing-overdue-cust-${o.invoice_no}`}>{o.customer}</button>
                  <span className="text-[11px] text-slate-400 whitespace-nowrap">due {o.due_date}</span>
                  <span className="text-[11px] font-bold text-red-600 whitespace-nowrap">{days}d late</span>
                  <span className="font-mono font-semibold text-slate-900 whitespace-nowrap">{smart(o.outstanding)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className="h-8 w-8 rounded-md flex items-center justify-center" style={{ backgroundColor: (accent || "#111827") + "1A" }}>
          <Icon className="h-4 w-4" style={{ color: accent || "#111827" }} />
        </span>
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-2">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

export default function Billing({ embedded }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const elevated = ["super_admin", "management", "sales_head"].includes(user?.role);
  const [region, setRegion] = useState("All");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [customer, setCustomer] = useState(null);
  const [feedOpen, setFeedOpen] = useState(false);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const params = elevated && region !== "All" ? { region } : {};
  const { data: sum, isLoading } = useQuery({
    queryKey: ["billing-summary", region],
    queryFn: async () => (await api.get("/billing/summary", { params })).data,
  });
  const { data: inv } = useQuery({
    queryKey: ["billing-invoices", search, page, region, source],
    queryFn: async () => (await api.get("/billing/invoices", { params: { ...params, q: search, page, ...(source ? { source } : {}) } })).data,
  });
  const { data: feed } = useQuery({
    queryKey: ["billing-daily-feed"],
    queryFn: async () => (await api.get("/billing/daily-feed")).data,
    enabled: feedOpen,
  });

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/billing/import", fd);
      toast.success(`Imported — ${data.created} new, ${data.updated} updated of ${data.received} lines`);
      qc.invalidateQueries({ queryKey: ["billing-summary"] });
      qc.invalidateQueries({ queryKey: ["billing-invoices"] });
    } catch (err) { toast.error(apiError(err)); } finally { setImporting(false); }
  };

  if (isLoading || !sum) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const t = sum.totals || {};
  const monthRows = (sum.by_month || []).map((m) => ({ name: m.month, v: Number((m.taxable / 1e7).toFixed(2)) }));
  const regionRows = (sum.by_region || []).map((m) => ({ name: m.region, v: Number((m.taxable / 1e7).toFixed(2)) }));

  return (
    <div data-testid="billing-page">
      {!embedded && <PageHeader title="Billing" subtitle={`Milestone ERP invoices · FY 2026-27${sum.as_of ? ` · latest invoice ${sum.as_of}` : ""}${sum.scope_region !== "All" ? ` · ${sum.scope_region} region` : ""}`}>
        {elevated && (
          <Select value={region} onValueChange={(v) => { setRegion(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="billing-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["All", "North", "West", "South", "East", "Exports"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {user?.role === "super_admin" && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={doImport} data-testid="billing-import-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="h-9 gap-1.5" data-testid="billing-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import Excel
            </Button>
          </>
        )}
      </PageHeader>}

      {sum.last_push && (
        <Card className="px-4 py-2.5 mb-5 border-emerald-200 bg-emerald-50/60" data-testid="billing-last-push-banner">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Zap className="h-4 w-4 text-emerald-600 shrink-0" />
            <span className="text-sm font-semibold text-slate-800">Daily Milestone feed</span>
            <span className="text-xs text-slate-500">last push {timeAgo(sum.last_push.at)} · {new Date(sum.last_push.at).toLocaleString("en-IN")}</span>
            <span className="text-xs font-mono text-slate-600">
              {sum.last_push.received} lines · {sum.last_push.created ?? 0} new · {sum.last_push.updated ?? 0} updated
              {sum.last_push.taxable_total ? ` · ${smart(sum.last_push.taxable_total)} taxable` : ""}
            </span>
            <span className="text-[10px] text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 font-semibold">included in totals</span>
            <div className="ml-auto flex items-center gap-3">
              <button type="button" onClick={() => setFeedOpen((v) => !v)}
                className="text-xs text-emerald-700 font-semibold hover:underline" data-testid="billing-feed-toggle">
                {feedOpen ? "Hide details ↑" : "View push details ↓"}
              </button>
              <button type="button"
                onClick={() => { setSource("milestone"); setPage(1); document.querySelector('[data-testid="billing-invoice-table"]')?.scrollIntoView({ behavior: "smooth" }); }}
                className="text-xs text-emerald-700 font-semibold hover:underline" data-testid="billing-view-milestone-btn">
                Filter Milestone lines
              </button>
            </div>
          </div>
          {feedOpen && (
            <div className="mt-3 pt-3 border-t border-emerald-100" data-testid="billing-feed-details">
              {!feed ? <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div> : (
                <>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(feed.pushes || []).map((p) => (
                      <span key={p.id} className="text-[11px] px-2 py-1 rounded-full bg-white border border-emerald-100 text-slate-600 font-mono">
                        {new Date(p.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}: {p.received} lines · {p.created ?? 0} new
                        {p.taxable_total ? ` · ${smart(p.taxable_total)}` : ""}
                      </span>
                    ))}
                  </div>
                  {(feed.pushes?.[0]?.lines || []).length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                          <th className="py-1 pr-3">Invoice</th><th className="pr-3">Date</th><th className="pr-3">Customer</th>
                          <th className="pr-3">Project</th><th className="pr-3 text-right">Taxable</th><th className="pr-3">Region</th><th>Status</th>
                        </tr></thead>
                        <tbody>
                          {feed.pushes[0].lines.slice(0, 15).map((l, i) => (
                            <tr key={i} className="border-t border-emerald-50" data-testid={`billing-feed-line-${i}`}>
                              <td className="py-1 pr-3 font-mono">{l.invoice_no}</td>
                              <td className="pr-3 whitespace-nowrap">{l.invoice_date}</td>
                              <td className="pr-3"><button type="button" onClick={() => setCustomer(l.customer_name)} className="hover:text-brand hover:underline text-left">{l.customer_name}</button></td>
                              <td className="pr-3 text-slate-400 max-w-[160px] truncate">{l.project_name}</td>
                              <td className="pr-3 text-right font-mono font-semibold">{cr(l.taxable_value)}</td>
                              <td className="pr-3">{l.region}</td>
                              <td><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${l.status === "new" ? "bg-emerald-100 text-emerald-700" : l.status === "updated" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"}`}>{(l.status || "").toUpperCase()}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {feed.pushes[0].lines.length > 15 && <p className="text-[11px] text-slate-400 mt-1">Showing 15 of {feed.pushes[0].lines.length} lines — use "Filter Milestone lines" for the full list.</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Line details weren't recorded for this push — the next daily push will show them here.</p>
                  )}
                </>
              )}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Kpi icon={Banknote} label="Total Invoiced (incl. tax)" value={cr(t.invoiced)} sub={`${t.lines} line items`} accent="#10B981" testid="billing-kpi-invoiced" />
        <Kpi icon={Wallet} label="Taxable Value" value={cr(t.taxable)} accent="#6366F1" testid="billing-kpi-taxable" />
        <Kpi icon={Receipt} label="Invoices" value={t.invoices} accent="#0EA5E9" testid="billing-kpi-invoices" />
        <Kpi icon={CalendarDays} label="This Month (taxable)" value={cr(t.month_taxable)} accent="#F59E0B" testid="billing-kpi-month" />
      </div>

      <PaymentTracking pay={sum.payments} onCustomer={setCustomer} />

      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <Card className="p-5" data-testid="billing-monthly-chart">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-2">Monthly billing (INR Cr, taxable)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthRows} margin={{ top: 20, right: 8, left: -24, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v) => [`${v}`, "Taxable"]} />
              <Bar dataKey="v" radius={[3, 3, 0, 0]} fill="#3b9fe0" isAnimationActive={false}>
                <LabelList dataKey="v" position="top" style={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5" data-testid="billing-region-chart">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-2">By region (INR Cr, taxable)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={regionRows} margin={{ top: 20, right: 8, left: -24, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v) => [`${v}`, "Taxable"]} />
              <Bar dataKey="v" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {regionRows.map((r, i) => <Cell key={i} fill={["#f04935", "#3b9fe0", "#35b37e", "#f2c037", "#7e57c2"][i % 5]} />)}
                <LabelList dataKey="v" position="top" style={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {(sum.by_channel || []).map((c) => (
              <span key={c.channel} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">{c.channel}: {cr(c.taxable)}</span>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <Card className="p-5" data-testid="billing-by-salesperson">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">By salesperson</h3>
          <div className="space-y-1.5">
            {(sum.by_salesperson || []).slice(0, 10).map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate">{s.name}</span>
                <span className="font-mono font-semibold text-slate-900">{cr(s.taxable)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5" data-testid="billing-top-customers">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">Top customers</h3>
          <div className="space-y-1.5">
            {(sum.top_customers || []).slice(0, 10).map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm gap-2">
                <button type="button" onClick={() => setCustomer(s.name)} className="text-slate-700 truncate text-left hover:text-brand hover:underline" data-testid={`billing-topcust-${s.name}`}>{s.name}</button>
                <span className="font-mono font-semibold text-slate-900 whitespace-nowrap">{lac(s.taxable)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5" data-testid="billing-top-dealers">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">Top dealers</h3>
          <div className="space-y-1.5">
            {(sum.top_dealers || []).length === 0 && <p className="text-sm text-slate-400">No dealer-channel billing in scope.</p>}
            {(sum.top_dealers || []).slice(0, 10).map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm gap-2">
                <span className="text-slate-700 truncate">{s.name}</span>
                <span className="font-mono font-semibold text-slate-900 whitespace-nowrap">{lac(s.taxable)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5" data-testid="billing-invoice-table">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5"><FileSpreadsheet className="h-4 w-4 text-brand" /> Invoice lines {inv ? `(${inv.total})` : ""}</h3>
          <div className="flex items-center gap-2">
            <Select value={source || "all"} onValueChange={(v) => { setSource(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="billing-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="milestone">Milestone (any)</SelectItem>
                <SelectItem value="milestone_billing_api">Milestone — Billing API</SelectItem>
                <SelectItem value="milestone_billing_csv">Milestone — Daily CSV</SelectItem>
                <SelectItem value="billing_excel_upload">Excel upload</SelectItem>
                <SelectItem value="legacy">Legacy / unmarked</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setSearch(q); setPage(1); } }}
                placeholder="Invoice / customer / project…" className="h-9 pl-8 w-56" data-testid="billing-search-input" />
            </div>
            <Button variant="outline" className="h-9" onClick={() => { setSearch(q); setPage(1); }} data-testid="billing-search-btn">Search</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Invoice", "Date", "Customer", "Project", "Item", "Qty", "Taxable", "Region", "Salesperson", "Payment", "Source"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(inv?.items || []).map((r, i) => {
                const [sl, sc] = SRC_BADGE[r.source] || ["Legacy", "bg-slate-50 text-slate-400"];
                const [pl, pc] = payBadge(r);
                return (
                <TableRow key={`${r.invoice_no}-${r.line_no}-${i}`} data-testid={`billing-row-${i}`}>
                  <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{r.invoice_date}</TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">
                    <button type="button" onClick={() => setCustomer(r.customer_name)} className="hover:text-brand hover:underline text-left truncate max-w-full" data-testid={`billing-row-cust-${i}`}>{r.customer_name}</button>
                  </TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate text-slate-500">{r.project_name}</TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate text-slate-500">{r.item_description}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.qty}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{cr(r.taxable_value)}</TableCell>
                  <TableCell className="text-xs">{r.region}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{r.sales_manager}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${pc}`}>{pl}</span>
                    {!CLOSED_STATUSES.includes((r.payment_status || "").toLowerCase()) && Number(r.outstanding || 0) > 0 && (
                      <span className="block text-[10px] text-slate-400 font-mono mt-0.5">{cr(r.outstanding)} due</span>
                    )}
                  </TableCell>
                  <TableCell><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${sc}`}>{sl}</span></TableCell>
                </TableRow>
                );
              })}
              {(inv?.items || []).length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">No invoice lines found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        {inv && inv.pages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="billing-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-xs text-slate-500">Page {inv.page} / {inv.pages}</span>
            <Button variant="outline" size="sm" disabled={page >= inv.pages} onClick={() => setPage((p) => p + 1)} data-testid="billing-next-page"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </Card>
      <CustomerDrillDown name={customer} onClose={() => setCustomer(null)} />
    </div>
  );
}
